// SEO utility functions and page metadata

export const siteConfig = {
  siteName: 'Twisted Cloud Solutions',
  siteUrl: process.env.REACT_APP_SITE_URL || 'https://twistedcloudsolutions.com',
  defaultTitle: 'Twisted Cloud Solutions - Professional Cloud Services & AI Integration',
  defaultDescription: 'Transform your business with cutting-edge cloud solutions, AI integration, and mobile applications. Expert cloud consulting and development services.',
  defaultKeywords: 'cloud services, AI integration, mobile applications, cloud consulting, business automation, digital transformation',
  twitterHandle: '@twistedcloud',
  companyName: 'Twisted Cloud Solutions',
  defaultOgImage: '/images/og-default.jpg'
};

// Page-specific SEO data
export const pageMetadata = {
  home: {
    title: 'Professional Cloud Services & AI Integration',
    description: 'Transform your business with cutting-edge cloud solutions, AI integration, and mobile applications. Expert cloud consulting and development services.',
    keywords: 'cloud services, AI integration, mobile applications, cloud consulting, business automation, digital transformation',
    canonical: '/'
  },
  services: {
    aiIntegration: {
      title: 'AI Integration Services - Intelligent Business Solutions',
      description: 'Harness the power of artificial intelligence to automate processes, enhance decision-making, and drive innovation. Custom AI solutions tailored to your business needs.',
      keywords: 'AI integration, artificial intelligence, machine learning, business automation, intelligent systems, AI consulting',
      canonical: '/services/ai-integration'
    },
    intelligentBusinessNetworks: {
      title: 'Intelligent Business Networks - Connected Enterprise Solutions',
      description: 'Build smart, interconnected business networks that enable seamless collaboration, data sharing, and operational efficiency across your organization.',
      keywords: 'business networks, enterprise connectivity, intelligent networks, collaboration platforms, business integration',
      canonical: '/services/intelligent-business-networks'
    },
    mobileApplications: {
      title: 'Mobile Application Development - Cross-Platform Solutions',
      description: 'Create powerful, user-friendly mobile applications that drive engagement and deliver exceptional user experiences across iOS and Android platforms.',
      keywords: 'mobile app development, iOS apps, Android apps, cross-platform development, mobile solutions, app development',
      canonical: '/services/mobile-applications'
    }
  },
  caseStudies: {
    main: {
      title: 'Case Studies - Success Stories & Project Examples',
      description: 'Explore our portfolio of successful cloud solutions, AI integrations, and mobile applications. Real results from real clients across various industries.',
      keywords: 'case studies, project examples, success stories, client results, portfolio, cloud projects',
      canonical: '/case-studies'
    },
    ferments: {
      title: 'Ferments AI Sommelier Network Case Study',
      description: 'Discover how we built an AI-powered sommelier network that revolutionized wine discovery with personalized recommendations and expert connections.',
      keywords: 'AI sommelier, wine recommendation, mobile app, machine learning, case study, Ferments',
      canonical: '/case-studies/ferments-ai-sommelier'
    }
  },
  fermentsDemo: {
    title: 'Ferments Demo - AI Wine Sommelier Experience',
    description: 'Experience our AI-powered wine recommendation system. Discover personalized wine selections through intelligent algorithms and expert sommelier insights.',
    keywords: 'wine demo, AI sommelier demo, wine recommendations, Ferments demo, AI wine selection',
    canonical: '/ferments-demo'
  },
  privacy: {
    title: 'Privacy Policy - Twisted Cloud Solutions',
    description: 'Learn how Twisted Cloud Solutions collects, processes, and protects your personal information. Our commitment to data privacy and security.',
    keywords: 'privacy policy, data protection, security, information collection, data privacy, GDPR compliance',
    canonical: '/privacy'
  }
};

// Generate structured data for different page types
export const generateStructuredData = {
  organization: () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": siteConfig.companyName,
    "url": siteConfig.siteUrl,
    "logo": `${siteConfig.siteUrl}/images/logo.png`,
    "description": siteConfig.defaultDescription,
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "US"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "info@twistedcloudsolutions.com"
    },
    "sameAs": [
      "https://twitter.com/twistedcloud",
      "https://linkedin.com/company/twisted-cloud-solutions"
    ]
  }),

  website: () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": siteConfig.siteName,
    "url": siteConfig.siteUrl,
    "description": siteConfig.defaultDescription,
    "publisher": {
      "@type": "Organization",
      "name": siteConfig.companyName
    }
  }),

  service: (serviceData) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": serviceData.title,
    "description": serviceData.description,
    "provider": {
      "@type": "Organization",
      "name": siteConfig.companyName,
      "url": siteConfig.siteUrl
    },
    "areaServed": "Worldwide",
    "serviceType": "Technology Consulting"
  }),

  article: (articleData) => ({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": articleData.title,
    "description": articleData.description,
    "image": articleData.image || `${siteConfig.siteUrl}${siteConfig.defaultOgImage}`,
    "author": {
      "@type": "Organization",
      "name": siteConfig.companyName
    },
    "publisher": {
      "@type": "Organization",
      "name": siteConfig.companyName,
      "logo": {
        "@type": "ImageObject",
        "url": `${siteConfig.siteUrl}/images/logo.png`
      }
    },
    "datePublished": articleData.publishedAt,
    "dateModified": articleData.updatedAt || articleData.publishedAt
  }),

  breadcrumb: (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${siteConfig.siteUrl}${item.path}`
    }))
  })
};

// Utility function to get meta data for a specific page
export const getPageMetadata = (pageKey, subPageKey = null) => {
  if (subPageKey && pageMetadata[pageKey] && pageMetadata[pageKey][subPageKey]) {
    return pageMetadata[pageKey][subPageKey];
  }
  
  if (pageMetadata[pageKey]) {
    return pageMetadata[pageKey];
  }
  
  // Return default metadata if page not found
  return {
    title: siteConfig.defaultTitle,
    description: siteConfig.defaultDescription,
    keywords: siteConfig.defaultKeywords,
    canonical: '/'
  };
};

// Utility function to generate Open Graph image URL
export const getOgImageUrl = (imagePath) => {
  if (!imagePath) return `${siteConfig.siteUrl}${siteConfig.defaultOgImage}`;
  if (imagePath.startsWith('http')) return imagePath;
  return `${siteConfig.siteUrl}${imagePath}`;
};